import clsx from 'clsx';
import { History } from 'lucide-react';
import type { Deposit, DepositLedgerEntry } from '@/domain/types';
import { aud, simTimeFull } from '@/utils/format';

/** Label + direction for each movement kind (mirrors depositPaidAud's signs). */
const MOVEMENT_META: Record<DepositLedgerEntry['kind'], { label: string; sign: 1 | -1 }> = {
  paid: { label: 'Paid', sign: 1 },
  topup: { label: 'Top-up', sign: 1 },
  'rollover-in': { label: 'Rollover in', sign: 1 },
  reinstate: { label: 'Reinstate', sign: 1 },
  'transfer-in': { label: 'Transfer in', sign: 1 },
  'rollover-out': { label: 'Rollover out', sign: -1 },
  forfeit: { label: 'Forfeit', sign: -1 },
  redeemed: { label: 'Redeemed', sign: -1 },
  refunded: { label: 'Refunded', sign: -1 },
  'transfer-out': { label: 'Transfer out', sign: -1 },
};

function targetLabel(e: DepositLedgerEntry): string {
  if (e.toBookingId) return `→ ${e.toBookingId}`;
  if (e.fromBookingId) return `from ${e.fromBookingId}`;
  if (e.counterpartDepositId) return `↔ ${e.counterpartDepositId}`;
  if (e.toPieceId) return `→ ${e.toPieceId}`;
  return '';
}

/**
 * Expandable movement history for a deposit (G2): every paid / top-up /
 * rollover / forfeit / reinstate as a linked ledger entry. Shared by the
 * deposits ledger row drawer and the customer record deposit block.
 */
export default function DepositMovementDrawer({ deposit }: { deposit: Deposit }) {
  const ledger = deposit.ledger ?? [];
  return (
    <div
      data-testid={`deposit-movements-${deposit.id}`}
      className="rounded-lg border border-dashed border-gold-dim/40 bg-ink-black/40"
    >
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gold-dim/15">
        <History size={12} className="text-gold-dim" />
        <span className="label-small-caps text-cream-soft">Movement history</span>
      </div>
      {ledger.length === 0 ? (
        <p className="px-3 py-3 font-serif text-xs text-cream-soft">No movements recorded yet.</p>
      ) : (
        <ul className="divide-y divide-gold-dim/10">
          {ledger.map((e, i) => {
            const m = MOVEMENT_META[e.kind];
            const target = targetLabel(e);
            const detail = [target, e.note].filter(Boolean).join(' · ');
            return (
              <li
                key={i}
                className="grid grid-cols-[104px_84px_1fr_auto] items-center gap-3 px-3 py-2 text-xs"
              >
                <span
                  className={clsx(
                    'font-inter text-[10px] font-semibold uppercase tracking-widest',
                    m.sign < 0 ? 'text-blood' : 'text-mint',
                  )}
                >
                  {m.label}
                </span>
                <span
                  className={clsx(
                    'font-mono text-right tabular-nums',
                    m.sign < 0 ? 'text-blood' : 'text-mint',
                  )}
                >
                  {m.sign < 0 ? '−' : '+'}
                  {aud(e.amountAud)}
                </span>
                <span className="font-serif text-cream-soft truncate">
                  {detail}
                  {e.by ? <span className="text-cream-faint"> · by {e.by}</span> : null}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-cream-faint">
                  {simTimeFull(e.at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
